import { useLingui } from '@lingui/react/macro';
import { onlineManager } from '@tanstack/react-query';
import { createFileRoute, isNotFound, notFound, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import * as z from 'zod';

import { Post } from '#modules/finder/features/posts/components/Post';
import type { InviteInfo } from '#modules/finder/features/posts/components/Post/InviteLanding';
import { NativePost } from '#modules/finder/features/posts/components/Post/NativePost';
import { getPostByIdQueryOptions } from '#modules/finder/features/posts/model/posts';
import { sessionNoticeSearch } from '#modules/finder/features/posts/model/sessionNoticeSearch';
import {
  INVITE_REDEEM_STATUS,
  INVITE_RESOLVE_FAILED,
  resolveInviteLink,
} from '#modules/finder/features/posts/model/sessions';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { safeCheckIsAuth } from '#modules/user/features/auth/utils/checkIsAuth';
import { DashboardLayout } from '#modules/user/features/dashboard/components/DashboardLayout';
import { ApiError } from '#features/api/utils/ApiError';
import { ContentNotFound } from '#features/error/components/ContentNotFound';
import { isContentNotFoundError } from '#features/error/utils/isContentNotFoundError';
import { Link } from '#features/routing';
import { seedCachedOwnPost } from '#features/routing/utils/nativeCachedRoutes';
import { envConfig } from '#constants/envConfig';
import { useSSR } from '#hooks/useSSR';
import { seo } from '#utils/seo';

const postSearchSchema = z.object({
  addSession: z.boolean().optional(),
  /**
   * «Пришёл войти в голос»: ставит карточка сессии с людьми в комнате (Home, полоса в
   * Discover). Страница кладёт намерение в стор, а входит блок голоса сам, когда пустит.
   */
  voice: z.enum(['join']).optional(),
  /**
   * Код пригласительной ссылки. Обязан быть в схеме: `validateSearch` вычищает всё, чего здесь
   * нет, и параметр терялся бы при первой же навигации — ровно так когда-то не работал
   * `?redirect=` у входа.
   */
  invite: z.string().optional(),
  /**
   * Сбор, за которым человек пришёл из уведомления. Ставит `ActorEnrichmentService` в ссылке
   * письма и пуша: раньше тап вёл на страницу поста целиком, и приглашённый искал нужную строку
   * глазами. Как и `invite`, обязан быть в схеме — иначе `validateSearch` его вычистит.
   */
  session: z.string().optional(),
  // A notification destination hint; it never grants access or supplies the session outcome.
  sessionNotice: sessionNoticeSearch,
  /**
   * Метка «аккаунт только что создан». Приезжает сюда после регистрации по приглашению: вход
   * через провайдера возвращает новичка на ту же заявку, а не на форму профиля. Как и остальные
   * параметры, обязана быть в схеме — `validateSearch` вычищает всё, чего здесь нет, и попап
   * онбординга метку просто не увидел бы. Строка и boolean обе живые: навигация внутри
   * приложения кладёт строку, разбор адреса отдаёт boolean.
   */
  newUser: z.union([z.boolean(), z.string()]).optional(),
});

/**
 * Разбор приглашения не должен ронять страницу: заявка открывается и без баннера, а вот пятисотка
 * вместо поста стоила бы нам и приглашённого, и обычного посетителя.
 *
 * Три исхода вместо прежних двух. Пустой ответ — кода не существует, и человеку об этом надо
 * сказать: раньше он молча попадал на обычную страницу поста, будто пришёл сам, хотя в адресе
 * стоит приглашение. Отказ сервиса — тоже не «мёртвая ссылка»: экран предложит обновить
 * страницу, а не отправит человека прочь.
 */
const unavailableInvite = (code: string, status: string): InviteInfo => ({
  code,
  status,
  sessionId: null,
  gameId: null,
  inviterUserId: null,
  inviterDisplayName: null,
  inviterAvatar: null,
  startedAt: null,
  expiredAt: null,
  teamLimit: null,
  acceptedCount: 0,
  freeSeats: 0,
});

const safeResolveInvite = async (code: string) => {
  try {
    const info = await resolveInviteLink({ code });
    return info ?? unavailableInvite(code, INVITE_REDEEM_STATUS.linkInactive);
  } catch (error) {
    console.warn('invite resolve failed', error);
    return unavailableInvite(code, INVITE_RESOLVE_FAILED);
  }
};

/** TanStack Query бросает CancelledError без своего класса в публичном API — сверяем по имени. */
const isCancelledError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'CancelledError';

export const Route = createFileRoute('/{-$locale}/_layout/posts/$postId')({
  validateSearch: zodValidator(postSearchSchema),
  loader: async ({ params: { postId }, context, location }) => {
    // Auth state drives the dashboard chrome (login vs profile). SSR-safe: safeCheckIsAuth
    // has a hard timeout covering the server path and falls back to isAuth=false.
    const { isAuth } = await safeCheckIsAuth();

    // Приглашение разбираем здесь, а не в компоненте: код приходит адресом, и заявке он нужен
    // уже готовым — шапка «X зовёт тебя играть» рисуется поверх обложки игры первым же кадром,
    // без второго круга запросов.
    const inviteCode = (location.search as { invite?: string })?.invite;
    const nativeClient = import.meta.env.VITE_IS_NATIVE === 'true' && typeof window !== 'undefined';
    const offline = nativeClient && !onlineManager.isOnline();
    const invite = inviteCode
      ? offline
        ? unavailableInvite(inviteCode, INVITE_RESOLVE_FAILED)
        : await safeResolveInvite(inviteCode)
      : null;

    try {
      const options = getPostByIdQueryOptions({ id: postId });
      if (nativeClient) {
        seedCachedOwnPost(context.queryClient, postId);
        // A native route never waits on a detail read: the mounted gate owns loading/reconnect.
        // This also covers losing connectivity after the tap, before a retry goes on pause.
        const cached = context.queryClient.getQueryData(options.queryKey);
        if (cached === null) throw notFound();
        return { post: cached ?? null, isAuth, invite };
      }
      const post = await context.queryClient.ensureQueryData(options);

      if (!post) {
        throw notFound();
      }

      return { post, isAuth, invite };
    } catch (error) {
      // notFound() thrown above lands here too — let it through to notFoundComponent.
      if (isNotFound(error)) throw error;

      // Отмена — не сбой: запрос обрывается, когда его инвалидируют или пользователь уходит
      // со страницы. Раньше это писалось в консоль как ошибка и попадало в отчёты, маскируя
      // настоящие поломки (прод, 04–05.08.2026).
      if (!isCancelledError(error)) {
        console.error(error);
      }

      // The gateway reports a missing post as a GraphQL error over HTTP 200, so match
      // on the code rather than the status.
      if (isContentNotFoundError(error) || (error instanceof ApiError && error.status === 404)) {
        throw notFound();
      }

      // Don't crash the route — component will retry via useSuspenseQuery
      return { post: null, isAuth, invite };
    }
  },
  notFoundComponent: PostNotFound,
  head: ({ loaderData }) => {
    // LFG-посты эфемерны и почти одинаковы, поэтому поиск считает их малоценными:
    // Яндекс волнами индексировал и выбрасывал их (~2000 событий удаления с мая 2026),
    // что держало ИКС хоста на дне. `noindex` убирает пост из индекса, но оставляет
    // страницу краулябельной — превью в Discord/Telegram по-прежнему строится из og:image
    // (в отличие от Disallow в robots.txt, который закрыл бы страницу целиком).
    // `follow` — чтобы внутренние ссылки поста продолжали обходиться.
    const robots = { name: 'robots', content: 'noindex, follow' };

    if (loaderData?.post) {
      const updatedAt = loaderData.post.updatedAt;
      const updatedTime = updatedAt ? new Date(updatedAt).getTime() : null;

      return {
        meta: [
          ...seo({
            image: `${envConfig.HOST}/api/og/posts/${loaderData.post.id}/${updatedTime}`,
            url: `${envConfig.HOST}/posts/${loaderData.post.id}`,
          }),
          robots,
        ],
      };
    }

    return {
      meta: [robots],
    };
  },
  component: RouteComponent,
  // Public, crawlable post page — lives OUTSIDE the client-only `_dashboard` subtree
  // (which is ssr:false and would cascade-disable SSR here) so TanStack Start renders
  // head() server-side and Telegram/Discord/FB crawlers pick up the post-specific
  // og:image (/api/og/posts/<id>/...). The Post body is still client-gated
  // (Post/index.tsx: motion + useBreakpointValue under isBrowser); DashboardLayout is
  // SSR-safe, so the dashboard chrome is preserved and the /posts/:id URL is unchanged.
  ssr: true,
});

function PostNotFound() {
  // Not the loader's isAuth: the loader threw, so notFoundComponent gets no loaderData.
  const isAuth = useIsAuth();

  return (
    <DashboardLayout isAuth={isAuth}>
      <PostNotFoundContent />
    </DashboardLayout>
  );
}

function PostNotFoundContent() {
  const { t } = useLingui();
  const search = useSearch({ strict: false }) as { session?: string };
  const isAuth = useIsAuth();
  return (
    <ContentNotFound
      title={search.session ? t`This session is no longer available.` : t`notFound.post.title`}
      description={
        search.session
          ? t`The session may have been deleted or you no longer have access to it. You can find another session.`
          : t`notFound.post.description`
      }
      // The post page is public: sending an anonymous visitor to the authed Discover
      // would just bounce them into sign-in, so they get the landing instead.
      action={
        isAuth ? (
          <Link to="/{-$locale}/dashboard/discover">{t`notFound.findTeammates`}</Link>
        ) : (
          <Link to="/{-$locale}">{t`goHome`}</Link>
        )
      }
    />
  );
}

function RouteComponent() {
  const { postId } = Route.useParams();
  const { addSession, voice } = Route.useSearch();
  const { isAuth: loaderIsAuth, invite } = Route.useLoaderData();
  const { isBrowser } = useSSR();

  return (
    // Капча этой странице не нужна: единственный её потребитель здесь — жалоба на пост или
    // комментарий, а ReportModal с недавних пор монтирует провайдер сам, пока открыт.
    <DashboardLayout isAuth={loaderIsAuth}>
      {/* Post fetches the post via useSuspenseQuery; if that runs on the server and the
          gateway fetch fails (e.g. transient error), the whole SSR response would 500/401.
          The og:image is emitted by head() (server-side) from the loader, so the body can
          stay client-only: SSR serves head + chrome, the post renders after hydration.

          Приглашение уехало внутрь заявки вместе с обложкой игры: отдельной полосой сверху оно
          висело над пустым экраном (тело страницы всё равно ждёт гидрации), и человек видел
          стену входа раньше, чем повод войти. Превью ссылки в мессенджерах это не задевает —
          его собирает head() на сервере. */}
      {isBrowser ? (
        import.meta.env.VITE_IS_NATIVE === 'true' ? (
          <NativePost
            notFound={<PostNotFoundContent />}
            postId={postId}
            addSession={addSession}
            voice={voice}
            invite={invite}
          />
        ) : (
          <Post postId={postId} addSession={addSession} voice={voice} invite={invite} />
        )
      ) : null}
    </DashboardLayout>
  );
}
